'use client';

/**
 * ca_preview only: the explorer and planner keep going below the map, as the
 * store's pages do, instead of dropping straight into the newsletter band.
 * Two things the store would put here: places to go next (real region
 * photography and real listing counts from our regions API) and the gear
 * rail. Loads when it nears the viewport; renders nothing without the flag.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useCaPreview } from '../../lib/caPreview';
import { withBase } from '../../lib/brand';
import { resolveRegionImage, isOwnMedia, sizedMedia, regionCreditLabel } from '../../lib/regionImage';
import StoreGearRail, { prefetchGear } from './StoreGearRail';
import { curatedRegionPhoto } from '../../lib/curatedRegionPhotos';

const PICKS = ['Grampians', 'Blue Mountains', 'Great Ocean Road', 'Flinders Ranges', 'Gippsland', 'Hunter Valley', 'Snowy Mountains', 'Fraser Coast', 'Margaret River'];

type RegionRow = {
  name: string; state?: string | null; image_src?: string | null; image_src_original?: string | null; entity_count?: number | null;
  image_attribution?: string | null; image_attribution_url?: string | null;
};

/**
 * One credit per card photo: the curated photo's own credit, else the stored
 * photo's (Wikimedia Commons CC licences require it; Unsplash is credited too).
 */
export function regionCredits(regions: RegionRow[]): { name: string; label: string; href?: string }[] {
  const out: { name: string; label: string; href?: string }[] = [];
  for (const r of regions) {
    const c = curatedRegionPhoto(r.name);
    if (c) { out.push({ name: r.name, label: c.credit.replace(/^Photo: /, '') }); continue; }
    const s = regionCreditLabel(r);
    if (s) out.push({ name: r.name, ...s });
  }
  return out;
}

/** Card-sized renditions: Wikimedia standard thumb (960; other widths 400), Unsplash width. */
export function cardSized(src: string): string {
  if (isOwnMedia(src)) return sizedMedia(src, 960);
  const wm = src.match(/^(https:\/\/upload\.wikimedia\.org\/wikipedia\/commons)\/((?!thumb\/)[0-9a-f]\/[0-9a-f]{2})\/([^/?#]+)$/i);
  if (wm) return `${wm[1]}/thumb/${wm[2]}/${wm[3]}/960px-${wm[3]}`;
  if (/^https:\/\/images\.unsplash\.com\//.test(src)) {
    return src.replace(/([?&])w=\d+/, '$1w=800').replace(/([?&])h=\d+/, '$1h=600');
  }
  return src;
}

function slug(name: string) {
  return name.toLowerCase().replace(/&/g, 'and').replace(/ +/g, '-').replace(/--+/g, '-');
}

function RegionCard({ r }: { r: RegionRow }) {
  const curated = curatedRegionPhoto(r.name);
  const full = curated ? curated.src : resolveRegionImage(r.image_src);
  const [src, setSrc] = useState(() => (curated ? curated.src : cardSized(full)));
  const [dead, setDead] = useState(false);
  if (dead) return null;
  return (
    <li className="ca-xm__card">
      <a className="ca-xm__a" href={withBase(`/regions/${slug(r.name)}`)}>
        <span className="ca-xm__img">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={src} alt="" loading="eager" decoding="async" style={curated?.focus ? { objectPosition: curated.focus } : undefined} onError={() => {
            // card rendition -> full copy -> original upstream URL -> drop the card
            const orig = curated ? '' : (r.image_src_original || '');
            if (src !== full) setSrc(full);
            else if (orig && src !== orig) setSrc(orig);
            else setDead(true);
          }} />
        </span>
        {r.state && <span className="ca-xm__eyebrow">{r.state}</span>}
        <span className="ca-xm__t">{r.name}</span>
        {!!r.entity_count && <span className="ca-xm__n">{r.entity_count.toLocaleString()} places to stay</span>}
      </a>
    </li>
  );
}

export default function ExplorerMore() {
  const preview = useCaPreview();
  const ref = useRef<HTMLDivElement>(null);
  const [near, setNear] = useState(false);
  const [regions, setRegions] = useState<RegionRow[] | null>(null);

  // The gear rail paints from its snapshot; start the live prices now.
  useEffect(() => {
    if (preview) prefetchGear('general');
  }, [preview]);

  useEffect(() => {
    if (!preview || near) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') { setNear(true); return; }
    const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) setNear(true); }, { rootMargin: '1600px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [preview, near]);

  useEffect(() => {
    if (!near) return;
    let live = true;
    fetch('https://app.trip-planner.com.au/api/regions', { headers: { Accept: 'application/json' } })
      .then((r) => (r.ok ? r.json() : []))
      .then((j: RegionRow[] | { regions?: RegionRow[] }) => {
        if (!live) return;
        const list = Array.isArray(j) ? j : j.regions ?? [];
        const picked = PICKS.map((n) => list.find((r) => r.name === n)).filter(
          (r): r is RegionRow => !!r && !!r.image_src,
        );
        setRegions(picked.slice(0, 6));
      })
      .catch(() => live && setRegions([]));
    return () => { live = false; };
  }, [near]);

  if (!preview) return null;

  return (
    <div ref={ref} className="ca-xm" data-testid="explorer-more">
      {regions && regions.length > 0 && (
        <section className="ca-xm__sec" aria-labelledby="ca-xm-regions">
          <div className="ca-xm__head">
            <div>
              <h2 id="ca-xm-regions" className="ca-xm__h">Where to next</h2>
              <p className="ca-xm__lede">Six regions worth the drive</p>
            </div>
            <a className="ca-xm__link" href={withBase('/regions')}>All regions <span aria-hidden="true">→</span></a>
          </div>
          <ul className="ca-xm__grid" role="list">
            {regions.map((r) => <RegionCard key={r.name} r={r} />)}
          </ul>
          {(() => {
            const credits = regionCredits(regions);
            if (!credits.length) return null;
            return (
              <p className="ca-xm__credits">
                Photos.{' '}
                {credits.map((c, i) => (
                  <React.Fragment key={c.name}>
                    {c.name}:{' '}
                    {c.href ? <a href={c.href} target="_blank" rel="noopener noreferrer">{c.label}</a> : c.label}
                    {i < credits.length - 1 ? '. ' : '.'}
                  </React.Fragment>
                ))}
              </p>
            );
          })()}
        </section>
      )}
      {(
        <StoreGearRail
          context="general"
          heading="Pack for the road"
          lede="Camp gear we stock, ready for the trip"
          limit={4}
          className="ca-sg--explorer"
          id="ca-xm-gear"
        />
      )}
    </div>
  );
}
